import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import SearchableSelect from '../../components/SearchableSelect';
import masterUserAdminService from '../../services/masterUserAdminService';
import tenantAdminService from '../../services/tenantAdminService';
import { MasterUserAdmin, TenantAdmin } from '../../types';
import { getApiErrorMessage } from '../../utils/apiError';

const formatDateTime = (value?: string): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('es-AR');
};

const formatDefaultTenant = (user: MasterUserAdmin): string => {
  if (user.defaultTenantCode || user.defaultTenantName) {
    const code = user.defaultTenantCode?.trim();
    const name = user.defaultTenantName?.trim();
    if (code && name) return `${code} — ${name}`;
    return code || name || '-';
  }
  return '-';
};

const MasterUsers: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [users, setUsers] = useState<MasterUserAdmin[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [tenantId, setTenantId] = useState<number | null>(null);
  const [tenants, setTenants] = useState<TenantAdmin[]>([]);
  const [loading, setLoading] = useState(false);

  const prevQueryRef = useRef({ searchTerm, tenantId, page, rowsPerPage });

  const tenantSelectOptions = useMemo(
    () =>
      tenants.map((tenant) => ({
        value: tenant.id,
        label: `${tenant.code} — ${tenant.name}`,
      })),
    [tenants],
  );

  useEffect(() => {
    const loadTenants = async () => {
      try {
        const data = await tenantAdminService.listPaged({
          pageNumber: 1,
          pageSize: 100,
        });
        setTenants(data.items);
      } catch (error: unknown) {
        enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar tenants para el filtro'), {
          variant: 'error',
          preventDuplicate: true,
        });
      }
    };
    void loadTenants();
  }, [enqueueSnackbar]);

  const fetchUsers = useCallback(
    async (pageNum: number, pageSize: number, search: string, filterTenantId: number | null) => {
      try {
        setLoading(true);
        const data = await masterUserAdminService.listPaged({
          pageNumber: pageNum + 1,
          pageSize,
          searchTerm: search.trim() || undefined,
          tenantId: filterTenantId ?? undefined,
        });
        setUsers(data.items);
        setTotalCount(data.totalCount);
      } catch (error: unknown) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        const message =
          status === 404
            ? 'El servidor no expone /api/admin/master-users. Falta desplegar el módulo en la API.'
            : getApiErrorMessage(error, 'Error al cargar usuarios maestros');
        enqueueSnackbar(message, { variant: 'error', preventDuplicate: true });
        setUsers([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  useEffect(() => {
    const prev = prevQueryRef.current;
    const searchChanged = prev.searchTerm !== searchTerm;
    const tenantChanged = prev.tenantId !== tenantId;
    prevQueryRef.current = { searchTerm, tenantId, page, rowsPerPage };

    const delay = searchChanged && !tenantChanged ? 300 : 0;

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      if (cancelled) return;
      void fetchUsers(page, rowsPerPage, searchTerm, tenantId);
    }, delay);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [page, rowsPerPage, searchTerm, tenantId, fetchUsers]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleToggleStatus = async (user: MasterUserAdmin) => {
    const nextActive = !user.isActive;
    if (!nextActive) {
      const label = user.displayName || user.username;
      const ok = window.confirm(
        `¿Desactivar el usuario "${label}"? No podrá iniciar sesión en el sistema.`,
      );
      if (!ok) return;
    }

    try {
      await masterUserAdminService.setActiveStatus(user.id, nextActive);
      enqueueSnackbar(
        nextActive ? 'Usuario activado correctamente' : 'Usuario desactivado correctamente',
        { variant: 'success' },
      );
      fetchUsers(page, rowsPerPage, searchTerm, tenantId);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cambiar el estado del usuario'), {
        variant: 'error',
      });
    }
  };

  const renderStatusChip = (isActive: boolean) => (
    <Chip
      label={isActive ? 'Activo' : 'Inactivo'}
      size="small"
      color={isActive ? 'success' : 'default'}
      variant={isActive ? 'filled' : 'outlined'}
    />
  );

  const renderActions = (user: MasterUserAdmin) => (
    <Stack direction="row" spacing={0.5} justifyContent="center">
      <Tooltip title="Editar">
        <IconButton
          size="small"
          color="primary"
          onClick={() => navigate(`/sistema/usuarios-maestros/${user.id}`)}
        >
          <EditIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title={user.isActive ? 'Desactivar' : 'Activar'}>
        <IconButton
          size="small"
          color={user.isActive ? 'warning' : 'success'}
          onClick={() => handleToggleStatus(user)}
        >
          {user.isActive ? <BlockIcon /> : <CheckCircleIcon />}
        </IconButton>
      </Tooltip>
    </Stack>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Usuarios maestros
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/sistema/usuarios-maestros/nuevo')}
            >
              Nuevo usuario
            </Button>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  placeholder="Buscar por usuario, nombre o email..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(0);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#1976d2' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#f8f9fa',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SearchableSelect
                  label="Tenant"
                  allowEmpty
                  emptyOptionLabel="Todos los tenants"
                  value={tenantId}
                  onChange={(value) => {
                    setTenantId(value);
                    setPage(0);
                  }}
                  options={tenantSelectOptions}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#f8f9fa',
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {!loading && users.length === 0 ? (
            <Stack spacing={2} alignItems="center" sx={{ py: 6 }}>
              <Typography variant="body1" color="text.secondary">
                {searchTerm.trim() || tenantId != null
                  ? 'No se encontraron usuarios con los filtros aplicados.'
                  : 'No hay usuarios registrados en eOneMaster.Users.'}
              </Typography>
              {!searchTerm.trim() && tenantId == null && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/sistema/usuarios-maestros/nuevo')}
                >
                  Crear el primer usuario
                </Button>
              )}
            </Stack>
          ) : isMobile ? (
            <Stack spacing={2}>
              {users.map((user) => (
                <Card key={user.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {user.displayName || user.username}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Usuario: {user.username}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Email: {user.email || '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Tenant predeterminado: {formatDefaultTenant(user)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Último acceso: {formatDateTime(user.lastLoginAt)}
                    </Typography>
                    {renderStatusChip(user.isActive)}
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {renderActions(user)}
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Usuario</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Tenant predeterminado</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Último acceso</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>{user.id}</TableCell>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>{user.displayName || '-'}</TableCell>
                      <TableCell>{user.email || '-'}</TableCell>
                      <TableCell>{formatDefaultTenant(user)}</TableCell>
                      <TableCell>{formatDateTime(user.lastLoginAt)}</TableCell>
                      <TableCell>{renderStatusChip(user.isActive)}</TableCell>
                      <TableCell align="center">{renderActions(user)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {totalCount > 0 && (
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default MasterUsers;
